import { useState, useEffect } from "react";
import { apiFetch } from "../../utils/api";
import { storeToken, clearAllTokens } from "../../utils/crossDomainAuth";

interface BrokerInfo {
  id: string;
  name: string;
  office_name: string;
  license_number?: string;
  phone?: string;
  email?: string;
  role: "broker" | "admin";
  trust_score: number;
  is_verified: boolean;
  is_suspended?: boolean;
  region_sido?: string;
  region_sigungu?: string;
  total_listings?: number;
  total_deals_closed?: number;
  nda_violations?: number;
  created_at?: string;
  verification_status?: "" | "pending" | "approved" | "rejected";
  verification_reject_reason?: string;
}

export function useAuth() {
  const [broker, setBroker] = useState<BrokerInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("broker_info");
    if (stored) {
      try {
        setBroker(JSON.parse(stored));
      } catch {
        localStorage.removeItem("broker_info");
      }
    }
    setLoading(false);
  }, []);

  const login = async (phone: string, password: string) => {
    const res = await apiFetch<{ token: string; broker: BrokerInfo; need_password_change?: boolean }>(
      "/api/auth/login",
      {
        method: "POST",
        body: JSON.stringify({ phone, password }),
      },
    );
    localStorage.setItem("broker_token", res.token);
    localStorage.setItem("broker_info", JSON.stringify(res.broker));
    storeToken(res.broker.role === "admin" ? "admin" : "partner", res.token);
    setBroker(res.broker);
    return res;
  };

  const signup = async (data: {
    name: string;
    phone: string;
    email?: string;
    password: string;
    office_name: string;
    representative_name?: string;
    business_number?: string;
    license_number: string;
    region_sido: string;
    region_sigungu: string;
  }) => {
    const res = await apiFetch<{ token: string; broker: BrokerInfo }>(
      "/api/auth/signup",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    localStorage.setItem("broker_token", res.token);
    localStorage.setItem("broker_info", JSON.stringify(res.broker));
    storeToken(res.broker.role === "admin" ? "admin" : "partner", res.token);
    setBroker(res.broker);
    return res;
  };

  const logout = () => {
    localStorage.removeItem("broker_token");
    localStorage.removeItem("broker_info");
    clearAllTokens();
    setBroker(null);
  };

  /** 서버에서 최신 정보 갱신 */
  const refresh = async () => {
    try {
      const res = await apiFetch<{ broker: BrokerInfo }>("/api/auth/me");
      localStorage.setItem("broker_info", JSON.stringify(res.broker));
      setBroker(res.broker);
    } catch {
      logout();
    }
  };

  return { broker, loading, login, signup, logout, refresh };
}
